const MAX_REQUESTS = 100;

/** Captured network request with timing and response data */
interface NetworkRequest {
  id: string;
  url: string;
  method: string;
  status: number | null | 'cors';
  statusText: string;
  timestamp: number;
  duration: number | null;
  requestHeaders: Record<string, string>;
  requestBody?: string;
  responseHeaders: Record<string, string>;
  responseBody?: string;
  error?: string;
  failed: boolean;
}

const networkRequestsRecording = 'network-recording-enabled';

/** URL prefixes the interceptor captures */
export const API_URL_WHITELIST = [
  // Prices API URLs
  'https://7kgank9rw2.execute-api.eu-west-1.amazonaws.com',
  'https://mlvzp7z15i.execute-api.eu-west-1.amazonaws.com',
];

/** Generates unique ID from timestamp + random string */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/** Checks URL against API_URL_WHITELIST */
function shouldTrackUrl(url: string): boolean {
  // most APIs include the word krakenflex so we can rely on that. For the others we check the whitelist
  return (
    url.includes('krakenflex') ||
    API_URL_WHITELIST.some((pattern) => url.startsWith(pattern))
  );
}

/** Placeholder for body truncation — currently passthrough */
function truncateBody(body: string | undefined): string | undefined {
  if (!body) return undefined;
  return body;
}

/** Converts Headers instance or plain object to flat Record */
function parseHeaders(
  headers: Headers | Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      result[key] = value;
    });
  } else {
    Object.assign(result, headers);
  }
  return result;
}

const SENSITIVE_HEADERS = ['authorization', 'x-api-key'];

function stripSensitiveHeaders(
  headers: Record<string, string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(headers).filter(
      ([key]) => !SENSITIVE_HEADERS.includes(key.toLowerCase()),
    ),
  );
}

/** Serialises BodyInit types to string representation */
async function parseBody(
  body: BodyInit | Document | null | undefined,
): Promise<string | undefined> {
  if (!body) return undefined;

  try {
    if (typeof body === 'string') {
      return body;
    }
    if (body instanceof FormData) {
      const entries: Record<string, string> = {};
      body.forEach((value, key) => {
        entries[key] = typeof value === 'string' ? value : '[File]';
      });
      return JSON.stringify(entries);
    }
    if (body instanceof Blob) {
      return `[Blob: ${body.type}, ${body.size} bytes]`;
    }
    if (body instanceof ArrayBuffer) {
      return `[ArrayBuffer: ${body.byteLength} bytes]`;
    }
    if (body instanceof URLSearchParams) {
      return body.toString();
    }
    if ('nodeType' in body && body.nodeType === 9) {
      return '[Document]';
    }
    return String(body);
  } catch {
    return '[Unable to parse body]';
  }
}

/** Extracts URL string from a fetch resource argument */
function getUrlFromResource(resource: Parameters<typeof fetch>[0]): string {
  if (typeof resource === 'string') return resource;
  if (resource instanceof URL) return resource.toString();
  return resource.url;
}

/** Monkey-patches fetch/XHR to capture matching network requests for dev tools */
class NetworkInterceptor {
  private requests: NetworkRequest[] = [];

  private recording = false;

  private originalFetch: typeof window.fetch;

  private originalXHROpen: typeof XMLHttpRequest.prototype.open;

  private originalXHRSend: typeof XMLHttpRequest.prototype.send;

  constructor() {
    this.originalFetch = window.fetch;
    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;

    // Load recording state from localStorage
    const recordingState = window.localStorage.getItem(
      networkRequestsRecording,
    );
    this.recording = recordingState === 'true';
  }

  /** Activates fetch and XHR interception */
  init() {
    this.interceptFetch();
    this.interceptXHR();
  }

  /** Prepends request, caps at MAX_REQUESTS. No-op if not recording */
  private addRequest(request: NetworkRequest) {
    if (!this.recording) return;

    this.requests.unshift(request);
    if (this.requests.length > MAX_REQUESTS) {
      this.requests = this.requests.slice(0, MAX_REQUESTS);
    }
  }

  /** Merges partial updates into existing request by ID */
  private updateRequest(id: string, updates: Partial<NetworkRequest>) {
    const index = this.requests.findIndex((req) => req.id === id);
    if (index !== -1) {
      this.requests[index] = {
        ...this.requests[index],
        ...updates,
      } as NetworkRequest;
    }
  }

  /** Wraps window.fetch to capture request/response data */
  private interceptFetch() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    window.fetch = async function interceptedFetch(
      ...args: Parameters<typeof fetch>
    ): Promise<Response> {
      const [resource, init] = args;
      const url = getUrlFromResource(resource);

      // Check whitelist - if URL doesn't match, pass through without tracking
      if (!shouldTrackUrl(url)) {
        return self.originalFetch.apply(window, args);
      }

      // Skip all tracking work when not recording
      if (!self.recording) {
        return self.originalFetch.apply(window, args);
      }

      const method = init?.method || 'GET';
      const id = generateId();
      const timestamp = Date.now();

      let requestHeaders: Record<string, string> = {};
      if (init?.headers) {
        if (init.headers instanceof Headers) {
          requestHeaders = parseHeaders(init.headers);
        } else if (Array.isArray(init.headers)) {
          init.headers.forEach(([key, value]) => {
            requestHeaders[key] = value;
          });
        } else {
          requestHeaders = init.headers as Record<string, string>;
        }
      }

      requestHeaders = stripSensitiveHeaders(requestHeaders);

      const requestBody = await parseBody(init?.body);

      const request: NetworkRequest = {
        id,
        url,
        method: method.toUpperCase(),
        status: null,
        statusText: '',
        timestamp,
        duration: null,
        requestHeaders,
        requestBody: truncateBody(requestBody),
        responseHeaders: {},
        failed: false,
      };

      self.addRequest(request);

      try {
        const response = await self.originalFetch.apply(window, args);
        const duration = Date.now() - timestamp;

        const responseHeaders = parseHeaders(response.headers);

        // Clone response to read body
        const clonedResponse = response.clone();
        let responseBody: string | undefined;

        try {
          const contentType = response.headers.get('content-type') || '';

          // Skip binary responses
          if (
            contentType.includes('image/') ||
            contentType.includes('video/') ||
            contentType.includes('audio/') ||
            contentType.includes('application/octet-stream')
          ) {
            responseBody = `[Binary content: ${contentType}]`;
          } else {
            const text = await clonedResponse.text();
            responseBody = text;
          }
        } catch {
          responseBody = '[Unable to read response]';
        }

        self.updateRequest(id, {
          status: response.status,
          statusText: response.statusText,
          duration,
          responseHeaders,
          responseBody: truncateBody(responseBody),
          failed: response.status >= 400,
        });

        return response;
      } catch (error) {
        const duration = Date.now() - timestamp;
        const isCorsError =
          error instanceof TypeError && error.message === 'Failed to fetch';
        self.updateRequest(id, {
          duration,
          error: error instanceof Error ? error.message : String(error),
          status: isCorsError ? 'cors' : undefined,
          failed: true,
        });
        throw error;
      }
    };
  }

  /** Wraps XHR open/send to capture request/response data */
  private interceptXHR() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    XMLHttpRequest.prototype.open = function interceptedOpen(
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) {
      const urlString = typeof url === 'string' ? url : url.toString();

      // Check whitelist - if URL doesn't match, pass through without tracking
      if (!shouldTrackUrl(urlString)) {
        const isAsync = async !== undefined ? async : true;
        return self.originalXHROpen.call(
          this,
          method,
          url,
          isAsync,
          username,
          password,
        );
      }

      // Skip all tracking work when not recording
      if (!self.recording) {
        const isAsync = async !== undefined ? async : true;
        return self.originalXHROpen.call(
          this,
          method,
          url,
          isAsync,
          username,
          password,
        );
      }

      const id = generateId();
      const timestamp = Date.now();

      // eslint-disable-next-line no-underscore-dangle
      (this as any).__devtools_id = id;
      // eslint-disable-next-line no-underscore-dangle
      (this as any).__devtools_method = method.toUpperCase();
      // eslint-disable-next-line no-underscore-dangle
      (this as any).__devtools_url = urlString;
      // eslint-disable-next-line no-underscore-dangle
      (this as any).__devtools_timestamp = timestamp;

      const request: NetworkRequest = {
        id,
        url: urlString,
        method: method.toUpperCase(),
        status: null,
        statusText: '',
        timestamp,
        duration: null,
        requestHeaders: {},
        responseHeaders: {},
        failed: false,
      };

      self.addRequest(request);

      // async defaults to true if not provided
      const isAsync = async !== undefined ? async : true;
      return self.originalXHROpen.call(
        this,
        method,
        url,
        isAsync,
        username,
        password,
      );
    };

    XMLHttpRequest.prototype.send = function interceptedSend(
      this: XMLHttpRequest,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      // eslint-disable-next-line no-underscore-dangle
      const id = (this as any).__devtools_id;
      // eslint-disable-next-line no-underscore-dangle
      const timestamp = (this as any).__devtools_timestamp;

      if (id && self.recording) {
        // Capture request headers
        const requestHeaders: Record<string, string> = {};

        // Parse request body
        parseBody(body)
          .then((parsedBody) => {
            self.updateRequest(id, {
              requestHeaders,
              requestBody: truncateBody(parsedBody),
            });
          })
          .catch(() => {});

        this.addEventListener('load', () => {
          const duration = Date.now() - timestamp;
          const responseHeaders: Record<string, string> = {};

          const headerString = this.getAllResponseHeaders();
          headerString.split('\r\n').forEach((line) => {
            const parts = line.split(': ');
            const [key, value] = parts;
            if (parts.length === 2 && key && value) {
              responseHeaders[key] = value;
            }
          });

          let responseBody: string | undefined;
          try {
            const contentType = this.getResponseHeader('content-type') || '';

            if (
              contentType.includes('image/') ||
              contentType.includes('video/') ||
              contentType.includes('audio/')
            ) {
              responseBody = `[Binary content: ${contentType}]`;
            } else {
              responseBody = this.responseText;
            }
          } catch {
            responseBody = '[Unable to read response]';
          }

          self.updateRequest(id, {
            status: this.status,
            statusText: this.statusText,
            duration,
            responseHeaders,
            responseBody: truncateBody(responseBody),
            failed: this.status >= 400,
          });
        });

        this.addEventListener('error', () => {
          const duration = Date.now() - timestamp;
          self.updateRequest(id, {
            duration,
            error: 'Network error',
            failed: true,
          });
        });

        this.addEventListener('abort', () => {
          const duration = Date.now() - timestamp;
          self.updateRequest(id, {
            duration,
            error: 'Request aborted',
            failed: true,
          });
        });

        this.addEventListener('timeout', () => {
          const duration = Date.now() - timestamp;
          self.updateRequest(id, {
            duration,
            error: 'Request timeout',
            failed: true,
          });
        });
      }

      return self.originalXHRSend.call(this, body);
    };
  }

  /** Returns shallow copy of captured requests */
  getRequests(): NetworkRequest[] {
    return [...this.requests];
  }

  /** Empties captured requests array */
  clearRequests(): void {
    this.requests = [];
  }

  /** Enables capture, persists to localStorage */
  startRecording(): void {
    this.recording = true;
    window.localStorage.setItem(networkRequestsRecording, 'true');
  }

  /** Disables capture, persists to localStorage */
  stopRecording(): void {
    this.recording = false;
    window.localStorage.setItem(networkRequestsRecording, 'false');
  }

  /** Returns current recording state */
  isRecording(): boolean {
    return this.recording;
  }
}

// Initialize and expose globally
const interceptor = new NetworkInterceptor();

declare global {
  interface Window {
    __devTools__: {
      network: {
        getRequests: () => NetworkRequest[];
        clearRequests: () => void;
        startRecording: () => void;
        stopRecording: () => void;
        isRecording: () => boolean;
      };
    };
  }
}

/** Activates interception and exposes API on window.__devTools__.network */
export function initialiseNetworkInterceptor() {
  interceptor.init();

  // Expose API
  // eslint-disable-next-line no-underscore-dangle
  if (!window.__devTools__) {
    // eslint-disable-next-line no-underscore-dangle
    window.__devTools__ = {} as any;
  }

  // eslint-disable-next-line no-underscore-dangle
  window.__devTools__.network = {
    getRequests: () => interceptor.getRequests(),
    clearRequests: () => interceptor.clearRequests(),
    startRecording: () => interceptor.startRecording(),
    stopRecording: () => interceptor.stopRecording(),
    isRecording: () => interceptor.isRecording(),
  };
}
